/**
 * CustomerQueryGateway の Supabase 実装。
 *
 * - RPC（Postgres 関数）: supabase.rpc で実装。
 * - 目的特化の結合クエリ: 汎用 repository(table) の select 埋め込み（!inner）で実装
 *   （方針B改訂・2026-06-01。SDK で1クエリに書ける結合はここに寄せる）。
 */
import { supabase } from "@/integrations/supabase/client";
import { repository } from "@/infrastructure/repositories";
import type { Condition } from "@/domain/repositoryInterface/Repository";
import type {
  CustomerQueryGateway,
  CustomerSearchResult,
  CustomerVisitStats,
} from "@/domain/repositoryInterface/CustomerQueryGateway";

export class SupabaseCustomerQueryGateway implements CustomerQueryGateway {
  async idsWithNoVisitSince(shopId: string, sinceDate: string): Promise<string[]> {
    const { data, error } = await supabase.rpc("get_customer_ids_with_no_visit_since", {
      _shop_id: shopId,
      _since_date: sinceDate,
    });
    if (error) throw error;
    return (data as string[]) ?? [];
  }

  async visitStats(customerIds: string[]): Promise<CustomerVisitStats[]> {
    if (customerIds.length === 0) return [];
    const { data, error } = await supabase.rpc("get_customer_visit_stats", {
      _customer_ids: customerIds,
    });
    if (error) throw error;
    return (data as CustomerVisitStats[]) ?? [];
  }

  async searchAutocomplete(params: {
    query: string;
    shopId: string | null;
  }): Promise<CustomerSearchResult[]> {
    const term = `%${params.query.trim()}%`;
    const where: Condition[] = [
      { column: "deleted_at", op: "isNull" },
      {
        or: [
          { column: "full_name", op: "ilike", value: term },
          { column: "furigana", op: "ilike", value: term },
          { column: "phone1", op: "ilike", value: term },
        ],
      },
    ];

    let select = "id, full_name, furigana, phone1, email, member_type";
    if (params.shopId) {
      // 自店舗予約を持つ顧客だけに DB 側で絞る（!inner 結合）。
      // 旧: 予約を1万件引いて customer_id 導出→IN（1万超で切り捨て）の置換。
      select += ", bookings!inner(shop_id)";
      where.push({ column: "bookings.shop_id", op: "eq", value: params.shopId });
    }

    const { data } = await repository("customers").findMany<CustomerSearchResult>({
      select,
      where,
      orderBy: [{ column: "furigana", ascending: true }],
      page: { index: 0, size: 10 },
    });
    return data;
  }
}
